import React, { useState } from "react";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Calendar, Clock, User, Link as LinkIcon, CheckCircle, Star, DollarSign, MessageCircle } from "lucide-react";
import { Session } from "@/hooks/usePlayerSessions";
import { format, parseISO } from "date-fns";
import { ptBR } from 'date-fns/locale';
import { ReleasePaymentButton } from "./ReleasePaymentButton";
import { Button } from "@/components/ui/button";
import { ReviewModal } from "./ReviewModal";
import { markSessionAsCompleted, releaseSessionPayment } from "@/services/sessionService";
import { useToast } from "@/components/ui/use-toast";
import { TalkToMentorButton } from "@/components/mentor/profile/TalkToMentorButton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface PurchasedMentorshipCardProps {
  session: Session;
  onActionCompleted?: () => void;
}

export const PurchasedMentorshipCard: React.FC<PurchasedMentorshipCardProps> = ({
  session,
  onActionCompleted
}) => {
  const [isMarkingCompleted, setIsMarkingCompleted] = useState(false);
  const [isReleasingPayment, setIsReleasingPayment] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const { toast } = useToast();
  
  // Debug: Log do meeting_link recebido
  React.useEffect(() => {
    console.log(`🔗 [PurchasedMentorshipCard] Sessão ${session.id}:`, {
      meeting_link: session.meeting_link,
      hasMeetingLink: !!session.meeting_link,
      meetingLinkLength: session.meeting_link?.length || 0,
      status: session.status
    });
  }, [session.id, session.meeting_link, session.status]);
  
  // Função para gerar iniciais do nome
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };
  
  const formatDate = (dateStr: string) => {
    try {
      const date = parseISO(dateStr + 'T00:00:00');
      return format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
    } catch (e) {
      console.error("Error formatting date:", e);
      return dateStr;
    }
  };

  const getStatusBadge = () => {
    switch(session.status) {
      case "confirmado":
        return <Badge className="bg-blue-600">Confirmada</Badge>;
      case "pendente":
        return <Badge className="bg-yellow-600">Pendente</Badge>;
      case "concluido":
        return <Badge className="bg-green-600">Concluída</Badge>;
      case "cancelado":
        return <Badge className="bg-red-600">Cancelada</Badge>;
      default:
        return <Badge className="bg-gray-600">{session.status}</Badge>;
    }
  };
  
  const handleMarkCompleted = async () => {
    setIsMarkingCompleted(true);
    try {
      await markSessionAsCompleted(session.id);
      toast({
        title: "Mentoria marcada como realizada!",
        description: "A mentoria foi movida para sessões concluídas."
      });
      onActionCompleted?.();
    } catch (error) {
      console.error("Erro ao marcar como concluída:", error);
      toast({
        variant: "destructive",
        title: "Erro ao marcar mentoria",
        description: "Não foi possível marcar a mentoria como realizada."
      });
    } finally {
      setIsMarkingCompleted(false);
    }
  };

  const handleReleasePayment = async () => {
    setIsReleasingPayment(true);
    try {
      await releaseSessionPayment(session.id);
      toast({
        title: "Pagamento liberado!",
        description: "O pagamento foi liberado para o mentor."
      });
      onActionCompleted?.();
    } catch (error) {
      console.error("Erro ao liberar pagamento:", error);
      toast({
        variant: "destructive",
        title: "Erro ao liberar pagamento",
        description: "Não foi possível liberar o pagamento."
      });
    } finally {
      setIsReleasingPayment(false);
    }
  };

  const showMarkCompletedButton = session.status === "confirmado";
  const showReviewButton = session.status === "concluido" && !session.has_review;
  const showReleaseButton = session.status === "concluido" && !session.payment_released;
  const showLinkButton = session.status === "confirmado";
  const showTalkToMentorButton = session.status === "confirmado" || session.status === "pendente";

  return (
    <>
      <Card className="bg-[#1E2939] border-[#2A3749] overflow-hidden">
        <CardContent className="p-0">
          {/* Banner da Mentoria */}
          {session.mentorship_cover_url && (
            <div className="relative h-48 md:h-32 w-full overflow-hidden">
              <img 
                src={session.mentorship_cover_url} 
                alt={session.mentorship_title || `Mentoria de ${session.game_name}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/30"></div>
              <div className="absolute top-3 right-3">
                {getStatusBadge()}
              </div>
            </div>
          )}
          
          <div className="p-4">
            <div className="flex flex-col md:flex-row justify-between gap-4">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-white">
                    {session.mentorship_title || `Mentoria de ${session.game_name}`}
                  </h3>
                  {!session.mentorship_cover_url && getStatusBadge()}
                </div>
              
              <div className="flex items-center text-gray-300 gap-3">
                <Avatar className="w-8 h-8 border-2 border-[#00B6F0]">
                  <AvatarImage src={session.mentor_image_url || undefined} alt={session.mentor_name} />
                  <AvatarFallback className="bg-[#223342] text-[#00B6F0] text-xs">
                    {getInitials(session.mentor_name)}
                  </AvatarFallback>
                </Avatar>
                <span>Mentor: {session.mentor_name}</span>
              </div>
              
              <div className="flex items-center text-gray-300">
                <Calendar className="h-4 w-4 mr-2 text-[#00B6F0]" />
                Dia da mentoria: {formatDate(session.scheduled_date)}
              </div>
              
              <div className="flex items-center text-gray-300">
                <Clock className="h-4 w-4 mr-2 text-[#00B6F0]" />
                Horário da mentoria: {session.scheduled_start.substring(0, 5)} - {session.scheduled_end.substring(0, 5)}
              </div>
              
              {session.payment_released ? (
                <Badge variant="outline" className="bg-green-900/30 text-green-400 border-green-500">
                  Pagamento liberado
                </Badge>
              ) : session.status === "concluido" ? (
                <Badge variant="outline" className="bg-yellow-900/30 text-yellow-400 border-yellow-500">
                  Aguardando liberação do pagamento
                </Badge>
              ) : null}

              {session.has_review && (
                <Badge variant="outline" className="bg-blue-900/30 text-blue-400 border-blue-500">
                  Mentoria avaliada
                </Badge>
              )}
            </div>
            
            <div className="flex flex-col items-end justify-end gap-2">
              {/* Mark as Completed Button */}
              {showMarkCompletedButton && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full md:w-auto border-green-600 text-green-400 hover:bg-green-900/20"
                      disabled={isMarkingCompleted}
                    >
                      <CheckCircle className="mr-2 h-4 w-4" />
                      {isMarkingCompleted ? "Marcando..." : "Marcar como Realizada"}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#1A2233] border-[#222A35] text-white">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirmar mentoria realizada</AlertDialogTitle>
                      <AlertDialogDescription className="text-gray-400">
                        Tem certeza que a mentoria foi realizada? Esta ação moverá a sessão para "Sessões Concluídas" e permitirá que você avalie o mentor.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-transparent border-[#3A4759] text-gray-300 hover:bg-[#2A3749]">
                        Cancelar
                      </AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleMarkCompleted}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        Sim, foi realizada
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}

              {/* Review Button */}
              {showReviewButton && (
                <Button
                  variant="outline"
                  onClick={() => setShowReviewModal(true)}
                  className="w-full md:w-auto border-yellow-600 text-yellow-400 hover:bg-yellow-900/20"
                >
                  <Star className="mr-2 h-4 w-4" />
                  Avaliar Mentoria
                </Button>
              )}

              {/* Release Payment Button */}
              {showReleaseButton && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full md:w-auto border-[#00B6F0] text-[#00B6F0] hover:bg-blue-900/20"
                      disabled={isReleasingPayment}
                    >
                      <DollarSign className="mr-2 h-4 w-4" />
                      {isReleasingPayment ? "Liberando..." : "Liberar Fundos"}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#1A2233] border-[#222A35] text-white">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Liberar pagamento</AlertDialogTitle>
                      <AlertDialogDescription className="text-gray-400">
                        Confirma a liberação do pagamento para o mentor? Esta ação não pode ser desfeita.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-transparent border-[#3A4759] text-gray-300 hover:bg-[#2A3749]">
                        Cancelar
                      </AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleReleasePayment}
                        className="bg-[#00B6F0] hover:bg-[#0096CC] text-white"
                      >
                        Liberar Pagamento
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}

              {/* Talk to Mentor Button */}
              {showTalkToMentorButton && (
                <div className="w-full md:w-auto">
                  <TalkToMentorButton 
                    mentorId={session.mentor_id} 
                    mentorName={session.mentor_name}
                    hasPurchasedMentorship={true}
                  />
                </div>
              )}

              {/* Meeting Link Button */}
              {showLinkButton && (
                <div className="w-full md:w-auto">
                  {session.meeting_link && session.meeting_link.trim() !== "" ? (
                    <Button 
                      variant="outline"
                      className="w-full md:w-auto border-[#00B6F0] text-[#00B6F0] hover:bg-[#1E293B]"
                      asChild
                    >
                      <a 
                        href={session.meeting_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center"
                      >
                        <LinkIcon className="mr-2 h-4 w-4" /> 
                        Ver Link da Aula
                      </a>
                    </Button>
                  ) : (
                    <Button 
                      variant="outline"
                      className="w-full md:w-auto border-[#2A3749] text-gray-400 cursor-not-allowed"
                      disabled
                    >
                      <LinkIcon className="mr-2 h-4 w-4" /> 
                      Aguardando mentor inserir link
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        </CardContent>
      </Card>

      {/* Review Modal */}
      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        sessionId={session.id}
        mentorId={session.mentor_id}
        mentorName={session.mentor_name}
        onReviewSubmitted={() => {
          onActionCompleted?.();
        }}
      />
    </>
  );
};
